import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, Clock } from "lucide-react";
import { api } from "@/lib/api";

const VINTAGE_STYLE = {
  yes: "border-emerald-200 bg-emerald-50 text-emerald-900",
  no: "border-rose-200 bg-rose-50 text-rose-900",
  unknown: "border-amber-200 bg-amber-50 text-amber-900",
};

function VintageNote({ etsy }) {
  const v = etsy?.vintage;
  const head = v === "yes" ? "Looks old enough for Etsy Vintage" : v === "no" ? "Too new for Etsy Vintage" : "Check age before choosing Vintage";
  return (
    <div className={`rounded-lg border p-2.5 text-[11px] leading-relaxed ${VINTAGE_STYLE[v] || "border-slate-200 bg-slate-50 text-slate-700"}`} data-testid="etsy-vintage-note">
      <p className="flex items-center gap-1 font-semibold" data-testid="etsy-vintage-status"><Clock className="h-3 w-3" /> {etsy ? head : "Etsy's 20-year rule"}</p>
      <p className="mt-0.5">
        Etsy only allows items made in or before <b>{etsy?.cutoff_year || new Date().getFullYear() - 20}</b> in its Vintage section.
        {etsy?.made_year && etsy.made_year !== "unknown" ? <> Estimated made: <b data-testid="etsy-made-year">{etsy.made_year}</b>.</> : null}
        {etsy?.vintage_reason ? <> {etsy.vintage_reason}</> : null}
        {v === "no" ? " List it as handmade/supplies only if that's true, or skip Etsy." : null}
      </p>
    </div>
  );
}

export default function EtsyFields({ draft, channel, Row }) {
  const [etsy, setEtsy] = useState(draft.etsy?.tags?.length ? draft.etsy : null);
  const [busy, setBusy] = useState(false);
  const stale = etsy && etsy.for_title && etsy.for_title !== draft.title;

  const make = async () => {
    setBusy(true);
    try {
      const { title, category, condition, description, item_specifics } = draft;
      const { data } = await api.post(`/drafts/${draft.id}/etsy-tags`, { title, category, condition, description, item_specifics });
      setEtsy(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't make Etsy tags");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="quick-paste-etsy">
      <div className="rounded-lg border border-slate-200 p-2.5" data-testid="etsy-tags-box">
        <div className="flex items-center gap-2">
          <p className="flex-1 font-mono text-[10px] uppercase tracking-wider text-slate-500">Tags {etsy ? `· ${etsy.tags.length}/13` : ""}</p>
          <button type="button" onClick={make} disabled={busy} data-testid="etsy-make-tags-btn"
            className="inline-flex h-7 items-center gap-1 rounded-full px-3 text-[11px] font-semibold text-white disabled:opacity-60"
            style={{ backgroundColor: channel.color }}>
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
            {etsy ? "Remake" : "Make 13 tags"}
          </button>
        </div>
        {etsy ? (
          <>
            <div className="mt-2 flex flex-wrap gap-1" data-testid="etsy-tag-list">
              {etsy.tags.map((t, i) => (
                <button key={t} type="button" data-testid={`etsy-tag-${i}`}
                  onClick={() => navigator.clipboard.writeText(t).then(() => toast.success(`Copied "${t}"`))}
                  className="rounded-full border border-orange-200 bg-orange-50 px-2 py-0.5 text-[11px] text-orange-900 hover:bg-orange-100">{t}</button>
              ))}
            </div>
            {stale && <p className="mt-1.5 text-[11px] text-amber-700" data-testid="etsy-tags-stale">Title changed since these were made — tap Remake.</p>}
            <p className="mt-1.5 text-[11px] text-slate-500">Tap a tag to copy it — Etsy adds tags one at a time.</p>
          </>
        ) : (
          <p className="mt-1.5 text-[11px] text-slate-500" data-testid="etsy-tags-empty">AI makes 13 search tags (20 characters max each) from your title and item specifics.</p>
        )}
      </div>
      {etsy && <Row label="All tags" value={etsy.tags.join(", ")} hint="comma list" testid="etsy-tags-all" channel={channel} />}
      <VintageNote etsy={etsy} />
    </div>
  );
}
